import React, { useCallback, useEffect, useState, memo } from "react";
import styles from "./DocumentUploader.module.css";
import { Row, Col, Accordion, Button, Spinner } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import SelectInput from "../../../../../Reusables/components/SelectInput/SelectInput";
import DropFileInput from "../../../../../pages/reusable_components/drop_file_component/DropFileComponent";
import {
  deleteDocumentForClient,
  fetchDocumentForClient,
  fetchDocumentTypes,
  uploadDocumentForClient,
  updateDocumentMetadata,
} from "./DocumentUploader.service";
import { DocumentUploaderProps, ClientDocument, downloadFile } from "./DocumentUploader.utils";
import { FileInputTypes } from "../../../../../pages/client_onboarding/ClientsGrid/ClientsGrid.utils";
import { DropdownOption, DocumentClassOption, PendingFile, EditDraft } from "./DocumentUploader.types";
import { fetchOptions } from "../../../../client_onboarding/ClientsGrid/ClientsGrid.service";
import PdfViewer from "../../../../reusable_components/PdfViewer/PdfViewer";
import SpreadsheetViewer from "../../../../../Reusables/components/SpreadsheetViewer/SpreadsheetViewer";
import ImageViewer from "../../../../../Reusables/components/ImageViewer/ImageViewer";
import TextViewer from "../../../../../Reusables/components/TextViewer/TextViewer";
import WordDocumentViewer from "../../../../../Reusables/components/WordDocumentViewer/WordDocumentViewer";
import { FieldProps } from "../../../../../Reusables/components/InputFieldsController/InputFieldsController.utils";


/**
 * Returns a Bootstrap icon element based on the file type extension.
 */
export const getFileIcon = (fileName: string) => {
  const ext = fileName.split(".").pop()?.toLowerCase();

  switch (ext) {
    case "pdf":
      return <Icon.FilePdfFill size={16} color="#D32F2F" />;
    case "xlsx":
    case "xls":
    case "csv":
      return <Icon.FileEarmarkSpreadsheetFill size={16} color="#2E7D32" />;
    case "doc":
    case "docx":
      return <Icon.FileWordFill size={16} color="#1565C0" />;
    case "png":
    case "jpg":
    case "jpeg":
    case "gif":
    case "bmp":
    case "tiff":
    case "webp":
    case "svg":
      return <Icon.FileImageFill size={16} color="#6A1B9A" />;
    default:
      return <Icon.FileEarmarkFill size={16} color="#616161" />;
  }
};

const truncateFileName = (name: string, max = 26) =>
  name.length > max ? name.slice(0, max) + "…" : name;

interface PendingRowProps {
  pf: PendingFile;
  idx: number;
  referenceCode: string;
  folderOptions: DropdownOption[];
  classOptions: DocumentClassOption[];
  onMetaChange: (referenceCode: string, idx: number, field: keyof Omit<PendingFile, "file">, value: string) => void;
  onRemove: (referenceCode: string, idx: number) => void;
}

const PendingRow = memo(({ pf, idx, referenceCode, folderOptions, classOptions, onMetaChange, onRemove }: PendingRowProps) => {
  const subOptions = classOptions.find((c) => c.value === pf.classValue)?.sub_options ?? [];
  return (
    <div className={styles.pendingRow}>
      <div className={styles.pendingFileName}>
        {getFileIcon(pf.file.name)}
        <span title={pf.file.name}>{truncateFileName(pf.file.name)}</span>
      </div>
      <div className={styles.pendingMeta}>
        <div className={styles.metaSelect} data-field-variant="underline">
          <SelectInput
            field={{ type: "dropdown", id: "folder", label: "", value: pf.folder, placeholder: "Folder", options: folderOptions.map((o) => ({ id: String(o.id), value: o.value, label: o.label })), margins: { top: 0, right: 0, bottom: 0, left: 0 }, width: "100%" }}
            handleChange={(_, opt) => onMetaChange(referenceCode, idx, "folder", opt?.value ?? "")}
            handleBlur={() => {}} handleSelect={() => {}}
            menuPlacement="auto" disablePortal
          />
        </div>
        <div className={styles.metaSelect} data-field-variant="underline">
          <SelectInput
            field={{ type: "dropdown", id: "classValue", label: "", value: pf.classValue, placeholder: "Class", options: classOptions.map((o) => ({ id: String(o.id), value: o.value, label: o.label })), margins: { top: 0, right: 0, bottom: 0, left: 0 }, width: "100%" }}
            handleChange={(_, opt) => { onMetaChange(referenceCode, idx, "classValue", opt?.value ?? ""); onMetaChange(referenceCode, idx, "subClass", ""); }}
            handleBlur={() => {}} handleSelect={() => {}}
            menuPlacement="auto" disablePortal
          />
        </div>
        <div className={styles.metaSelect} data-field-variant="underline">
          <SelectInput
            field={{ type: "dropdown", id: "subClass", label: "", value: pf.subClass, placeholder: "Subclass", options: subOptions.map((s) => ({ id: String(s.id), value: s.value, label: s.label })), disabled: subOptions.length === 0, margins: { top: 0, right: 0, bottom: 0, left: 0 }, width: "100%" }}
            handleChange={(_, opt) => onMetaChange(referenceCode, idx, "subClass", opt?.value ?? "")}
            handleBlur={() => {}} handleSelect={() => {}}
            menuPlacement="auto" disablePortal
          />
        </div>
      </div>
      <button
        className={styles.removePendingBtn}
        title="Remove"
        onClick={() => onRemove(referenceCode, idx)}
      >
        <Icon.XLg size={12} />
      </button>
    </div>
  );
});

/**
 * DocumentUploader
 *
 * Accordion-based component for uploading and managing client documents.
 * Supports multi-file selection with optional per-document Folder/Class/SubClass metadata
 * collected in a staging area before the actual upload.
 */
const DocumentUploader: React.FC<DocumentUploaderProps> = ({
  clientMasterId,
  fields,
  disabled = false,
  onValidationChange,
  formFieldsByTab
}) => {
  const [uploadProgressByDocType, setUploadProgressByDocType] = useState<{
    [referenceCode: string]: boolean;
  }>({});
  const [existingDocuments, setExistingDocuments] = useState<{
    [referenceCode: string]: ClientDocument[];
  }>({});
  const [pendingFiles, setPendingFiles] = useState<{
    [referenceCode: string]: PendingFile[];
  }>({});
  const [previewFile, setPreviewFile] = useState<{ url: string; name: string } | null>(null);
  const [filteredFileTypes, setFilteredFileTypes] = useState<FileInputTypes[]>([]);
  const [apiProgress, setApiProgress] = useState<boolean>(false);
  const [editingDocId, setEditingDocId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<EditDraft>({ folder: "", classValue: "", subClass: "" });
  const [editSaving, setEditSaving] = useState(false);

  const [folderOptions, setFolderOptions] = useState<DropdownOption[]>([]);
  const [classOptions, setClassOptions] = useState<DocumentClassOption[]>([]);

  useEffect(() => {
    Promise.all([
      fetchOptions("get_folders"),
      fetchOptions("get_document_classes"),
    ]).then(([folders, classes]) => {
      setFolderOptions(folders ?? []);
      setClassOptions(classes ?? []);
    });
  }, []);

  useEffect(() => {
    if (!formFieldsByTab) return;

    fetchDocumentTypes((types) => {
      const allFormFields = Object.values(formFieldsByTab).flat() as FieldProps[];

      const isFieldVisible = (formField: FieldProps): boolean => {
        if (formField.show === false) return false;
        const dependsOn = formField.dependsOn?.trim();
        if (!dependsOn) return true;
        const parentField = allFormFields.find((f) => f.allfields_code?.trim() === dependsOn);
        if (!parentField) return true;
        const parentCode = parentField.allfields_code?.trim();
        if (!parentCode) return true;
        const parentValue = fields?.[parentCode];
        const visibilityValues: any[] = formField.dependsOnVisibilityValues?.length
          ? formField.dependsOnVisibilityValues
          : [formField.dependsOnVisibilityValue];
        return visibilityValues.some(
          (v) => String(v).toLowerCase() === String(parentValue).toLowerCase()
        );
      };

      const filtered = types.filter((type: FileInputTypes) => {
        const fieldCode = type.FieldCode?.trim();
        const formField = allFormFields.find(
          (f) => f.allfields_code?.trim() === fieldCode
        );
        if (!formField || !isFieldVisible(formField)) return false;
        const lookupCode = formField.allfields_code?.trim();
        if (!lookupCode) return false;
        const savedValue = fields?.[lookupCode];
        return savedValue === true || savedValue === "true";
      });
      setFilteredFileTypes(filtered);
    }, setApiProgress, false);
  }, [formFieldsByTab, fields]);

  /** Compute and report document validation status */
  useEffect(() => {
    if (!onValidationChange) return;
    const missing = filteredFileTypes
      .filter(type => (existingDocuments[type.FieldCode]?.length ?? 0) === 0)
      .map(type => type.ReferenceDataValue || type.FieldName || "Document");
    onValidationChange({ isValid: missing.length === 0, missing });
  }, [filteredFileTypes, existingDocuments, onValidationChange]);

  /** Load existing documents per reference code. */
  const fetchExistingDocument = async (firmReferenceId: number, referenceCode: string) => {
    const data = await fetchDocumentForClient(clientMasterId, () => { }, referenceCode, firmReferenceId);
    if (data) {
      setExistingDocuments((prev) => {
        const updated = { ...prev, [referenceCode]: data };
        if (!previewFile && data.length > 0) {
          setPreviewFile({ url: data[0].BlobUrl, name: data[0].FileName });
        }
        return updated;
      });
    }
  };

  useEffect(() => {
    if (filteredFileTypes.length > 0 && clientMasterId) {
      filteredFileTypes.forEach((input) => {
        fetchExistingDocument(input.FirmReferenceDataId, input.FieldCode);
      });
    } else {
      setPreviewFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredFileTypes, clientMasterId]);

  /** Stage selected files — do NOT upload yet. Pre-fill is_default options. */
  const handleFileChange = useCallback((
    files: File[],
    _event: React.ChangeEvent<HTMLInputElement>,
    referenceCode: string
  ) => {
    if (!files.length) return;
    const defaultFolder = folderOptions.find((o) => o.is_default)?.value ?? "";
    const defaultClassOption = classOptions.find((o) => o.is_default);
    const defaultClassValue = defaultClassOption?.value ?? "";
    const defaultSubClass = defaultClassOption?.sub_options.find((s) => s.is_default)?.value ?? "";
    setPendingFiles((prev) => ({
      ...prev,
      [referenceCode]: [
        ...(prev[referenceCode] || []),
        ...files.map((file) => ({
          file,
          folder: defaultFolder,
          classValue: defaultClassValue,
          subClass: defaultSubClass,
        })),
      ],
    }));
  }, [folderOptions, classOptions]);

  const handleMetadataChange = useCallback((
    referenceCode: string,
    fileIndex: number,
    field: keyof Omit<PendingFile, "file">,
    value: string
  ) => {
    setPendingFiles((prev) => {
      const updated = [...(prev[referenceCode] || [])];
      updated[fileIndex] = { ...updated[fileIndex], [field]: value };
      return { ...prev, [referenceCode]: updated };
    });
  }, []);

  const handleRemovePending = useCallback((referenceCode: string, fileIndex: number) => {
    setPendingFiles((prev) => {
      const updated = [...(prev[referenceCode] || [])];
      updated.splice(fileIndex, 1);
      return { ...prev, [referenceCode]: updated };
    });
  }, []);

  const handlePendingUpload = async (referenceCode: string) => {
    const pending = pendingFiles[referenceCode] || [];
    if (!pending.length) return;

    setUploadProgressByDocType((prev) => ({ ...prev, [referenceCode]: true }));

    const documentMetaData = pending.map((p) => ({
      DocumentTypeCode: referenceCode,
      FileName: p.file.name,
      Status: "Completed",
      ...(p.folder && { Folder: p.folder }),
      ...(p.classValue && { Class: p.classValue }),
      ...(p.subClass && { SubClass: p.subClass }),
    }));

    const response = await uploadDocumentForClient(
      clientMasterId,
      documentMetaData,
      pending.map((p) => p.file),
      (progress) => {
        setUploadProgressByDocType((prev) => ({ ...prev, [referenceCode]: progress }));
      }
    );

    if (response?.success && response.data[0].Documents?.length > 0) {
      const pendingByName = Object.fromEntries(pending.map((p) => [p.file.name, p]));

      const uploadedMetadata: ClientDocument[] = response.data[0].Documents.map((doc: any) => {
        const source = pendingByName[doc.FileName];
        return {
          ClientDocumentId: doc.ClientDocumentId,
          FirmId: response.data[0].FirmId,
          ClientMasterId: response.data[0].ClientMasterId,
          ClientName: response.data[0].ClientName || "",
          FileName: doc.FileName,
          BlobUrl: doc.BlobUrl,
          DocumentTypeCode: doc.DocumentTypeCode,
          DocumentTypeName: "",
          Status: "Completed",
          Active: doc.Active,
          CreatedBy: "",
          CreatedDate: new Date().toISOString(),
          UpdatedBy: "",
          UpdatedDate: new Date().toISOString(),
          Folder: source?.folder || undefined,
          Class: source?.classValue || undefined,
          SubClass: source?.subClass || undefined,
        };
      });

      setExistingDocuments((prev) => {
        const merged = {
          ...prev,
          [referenceCode]: [...(prev[referenceCode] || []), ...uploadedMetadata],
        };
        const latest = uploadedMetadata[uploadedMetadata.length - 1];
        setPreviewFile({ url: latest.BlobUrl, name: latest.FileName });
        return merged;
      });

      setPendingFiles((prev) => ({ ...prev, [referenceCode]: [] }));
    }

    setUploadProgressByDocType((prev) => ({ ...prev, [referenceCode]: false }));
  };

  const handleEditStart = (file: ClientDocument) => {
    setEditingDocId(file.ClientDocumentId);
    setEditDraft({
      folder: file.Folder || "",
      classValue: file.Class || "",
      subClass: file.SubClass || "",
    });
  };

  const handleEditCancel = () => {
    setEditingDocId(null);
    setEditDraft({ folder: "", classValue: "", subClass: "" });
  };

  const handleEditSave = async (file: ClientDocument, referenceCode: string) => {
    setEditSaving(true);
    const response = await updateDocumentMetadata(
      clientMasterId,
      file.ClientDocumentId,
      file.FileName,
      referenceCode,
      editDraft.folder || undefined,
      editDraft.classValue || undefined,
      editDraft.subClass || undefined
    );

    if (response?.success) {
      setExistingDocuments((prev) => ({
        ...prev,
        [referenceCode]: prev[referenceCode].map((doc) =>
          doc.ClientDocumentId === file.ClientDocumentId
            ? { ...doc, Folder: editDraft.folder || undefined, Class: editDraft.classValue || undefined, SubClass: editDraft.subClass || undefined }
            : doc
        ),
      }));
      setEditingDocId(null);
      setEditDraft({ folder: "", classValue: "", subClass: "" });
    }
    setEditSaving(false);
  };

  /** Delete document by ID */
  const handleDeleteDocument = async (
    file: ClientDocument,
    referenceCode: string,
    clientDocumentId: number
  ) => {
    const confirmed = window.confirm(`Are you sure you want to delete ${file.FileName}?`);
    if (!confirmed) return;
    const success = await deleteDocumentForClient(clientDocumentId);
    if (success) {
      setExistingDocuments((prev) => ({
        ...prev,
        [referenceCode]: prev[referenceCode].filter((doc) => doc.ClientDocumentId !== clientDocumentId),
      }));
      if (previewFile?.url === file.BlobUrl) setPreviewFile(null);
    }
  };

  return (
    <>
      <Row>
        <Col md={5}>
          <div className={styles.filesViewerWrapper}>
            <Accordion className={styles.uploadFilesAccordion} defaultActiveKey="0">
              {filteredFileTypes.map((input, index) => {
                const pending = pendingFiles[input.FieldCode] || [];
                const isUploading = uploadProgressByDocType[input.FieldCode];
                const docLabel = input.ReferenceDataValue || input.FieldName || "Document";

                return (
                  <Accordion.Item eventKey={String(index)} key={input.FirmReferenceDataId}>
                    <Accordion.Header className={styles.uploadFilesAccordianHeader}>
                      <div className={styles.uploadFilesAccordianHeaderContent}>
                        <div>
                          {docLabel}
                          <span className={styles.requiredAsterisk}>*</span>
                        </div>
                        <div
                          className={
                            styles.existingFileInfo +
                            " " +
                            (existingDocuments[input.FieldCode]?.length > 0 ? styles.availableFiles : "")
                          }
                        >
                          {existingDocuments[input.FieldCode]?.length || 0}
                        </div>
                      </div>
                    </Accordion.Header>
                    <Accordion.Body className={styles.uploadFilesAccordianBody}>
                      <div className={styles.dropFileWrapper}>
                        <DropFileInput
                          onFileChange={(files: File[], event: React.ChangeEvent<HTMLInputElement>) =>
                            handleFileChange(files, event, input.FieldCode)
                          }
                          uploading={false}
                          message={`Upload your ${docLabel}`}
                          disabled={disabled}
                        />
                      </div>

                      {pending.length > 0 && (
                        <div className={styles.pendingSection}>
                          <div className={styles.pendingSectionTitle}>
                            <Icon.ClockHistory size={13} className={styles.pendingIcon} />
                            Pending Upload ({pending.length})
                          </div>

                          {pending.map((pf, idx) => (
                            <PendingRow
                              key={`${input.FieldCode}-${idx}-${pf.file.name}`}
                              pf={pf}
                              idx={idx}
                              referenceCode={input.FieldCode}
                              folderOptions={folderOptions}
                              classOptions={classOptions}
                              onMetaChange={handleMetadataChange}
                              onRemove={handleRemovePending}
                            />
                          ))}

                          <Button
                            size="sm"
                            className={styles.uploadPendingBtn}
                            disabled={isUploading}
                            onClick={() => handlePendingUpload(input.FieldCode)}
                          >
                            {isUploading ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-1" />
                                Uploading…
                              </>
                            ) : (
                              <>
                                <Icon.CloudUpload size={13} className="me-1" />
                                Upload {pending.length} file{pending.length !== 1 ? "s" : ""}
                              </>
                            )}
                          </Button>
                        </div>
                      )}

                      {existingDocuments[input.FieldCode]?.length > 0 && (
                        <div className={styles.uploadedFileList}>
                          <div className={styles.uploadedFileTitle}>Uploaded Files:</div>
                          {existingDocuments[input.FieldCode].map((file, idx) => {
                            const isEditing = editingDocId === file.ClientDocumentId;
                            return (
                              <div key={idx} className={styles.uploadedFileRow}>
                                <div className={styles.uploadedFileInfo}>
                                  <div className={styles.uploadedFileName}>
                                    {getFileIcon(file.FileName)}
                                    <span>{file.FileName}</span>
                                  </div>

                                  {isEditing ? (() => {
                                    const editSubOptions = classOptions.find((c) => c.value === editDraft.classValue)?.sub_options ?? [];
                                    return (
                                    <div className={styles.editMetaRow}>
                                      <div className={styles.metaSelect} data-field-variant="underline">
                                        <SelectInput
                                          field={{ type: "dropdown", id: "folder", label: "", value: editDraft.folder, placeholder: "Folder", options: folderOptions.map((o) => ({ id: String(o.id), value: o.value, label: o.label })), margins: { top: 0, right: 0, bottom: 0, left: 0 }, width: "100%" }}
                                          handleChange={(_, opt) => setEditDraft((d) => ({ ...d, folder: opt?.value ?? "" }))}
                                          handleBlur={() => {}} handleSelect={() => {}}
                                          menuPlacement="auto" disablePortal
                                        />
                                      </div>
                                      <div className={styles.metaSelect} data-field-variant="underline">
                                        <SelectInput
                                          field={{ type: "dropdown", id: "classValue", label: "", value: editDraft.classValue, placeholder: "Class", options: classOptions.map((o) => ({ id: String(o.id), value: o.value, label: o.label })), margins: { top: 0, right: 0, bottom: 0, left: 0 }, width: "100%" }}
                                          handleChange={(_, opt) => setEditDraft((d) => ({ ...d, classValue: opt?.value ?? "", subClass: "" }))}
                                          handleBlur={() => {}} handleSelect={() => {}}
                                          menuPlacement="auto" disablePortal
                                        />
                                      </div>
                                      <div className={styles.metaSelect} data-field-variant="underline">
                                        <SelectInput
                                          field={{ type: "dropdown", id: "subClass", label: "", value: editDraft.subClass, placeholder: "Subclass", options: editSubOptions.map((s) => ({ id: String(s.id), value: s.value, label: s.label })), disabled: editSubOptions.length === 0, margins: { top: 0, right: 0, bottom: 0, left: 0 }, width: "100%" }}
                                          handleChange={(_, opt) => setEditDraft((d) => ({ ...d, subClass: opt?.value ?? "" }))}
                                          handleBlur={() => {}} handleSelect={() => {}}
                                          menuPlacement="auto" disablePortal
                                        />
                                      </div>
                                      <div className={styles.editActions}>
                                        <button
                                          className={`${styles.editActionBtn} ${styles.saveBtn}`}
                                          title="Save changes"
                                          disabled={editSaving}
                                          onClick={() => handleEditSave(file, input.FieldCode)}
                                        >
                                          {editSaving
                                            ? <Spinner animation="border" size="sm" />
                                            : <Icon.CheckLg size={12} />
                                          }
                                        </button>
                                        <button
                                          className={`${styles.editActionBtn} ${styles.cancelBtn}`}
                                          title="Cancel"
                                          disabled={editSaving}
                                          onClick={handleEditCancel}
                                        >
                                          <Icon.XLg size={12} />
                                        </button>
                                      </div>
                                    </div>
                                    );
                                  })() : (
                                    <div className={styles.uploadedFileMeta}>
                                      {file.Folder && (
                                        <span className={styles.metaTag}>
                                          <Icon.Folder2 size={10} className="me-1" />
                                          {file.Folder}
                                        </span>
                                      )}
                                      {file.Class && (
                                        <span className={styles.metaTag}>
                                          <Icon.Tag size={10} className="me-1" />
                                          {file.Class}
                                        </span>
                                      )}
                                      {file.SubClass && (
                                        <span className={styles.metaTag}>
                                          <Icon.TagFill size={10} className="me-1" />
                                          {file.SubClass}
                                        </span>
                                      )}
                                      {!disabled && (
                                        <button
                                          className={styles.metaEditChip}
                                          onClick={() => handleEditStart(file)}
                                          title="Edit metadata"
                                        >
                                          <Icon.PencilSquare size={9} />
                                          <span>
                                            {!file.Folder && !file.Class && !file.SubClass
                                              ? "Add metadata"
                                              : "Edit"}
                                          </span>
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>

                                {!isEditing && (
                                  <div className={styles.uploadedFileActions}>
                                    <Icon.Eye
                                      className={styles.viewIcon}
                                      title="View Document"
                                      onClick={() => setPreviewFile({ url: file.BlobUrl, name: file.FileName })}
                                    />
                                    <Icon.Download
                                      className={styles.downloadIcon}
                                      title="Download Document"
                                      onClick={() => downloadFile(file.BlobUrl, file.FileName)}
                                    />
                                    {!disabled && (
                                      <Icon.Trash
                                        className={styles.deleteIcon}
                                        title="Delete Document"
                                        onClick={() => handleDeleteDocument(file, input.FieldCode, file.ClientDocumentId)}
                                      />
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          </div>
        </Col>

        <Col md={7}>
          <div className={styles.viewerWrapper}>
            {/\.xlsx$|\.xls$|\.csv$/i.test(previewFile?.url || "") ? (
              <SpreadsheetViewer preview={previewFile} />
            ) : /\.docx$|\.doc$/i.test(previewFile?.url || "") ? (
              <WordDocumentViewer preview={previewFile!!} />
            ) : /\.(jpg|jpeg|png|gif|bmp|tiff|webp|svg)$/i.test(previewFile?.url || "") ? (
              <ImageViewer preview={previewFile} />
            ) : /\.(txt|log|json|xml|yaml|yml|md|rtf)$/i.test(previewFile?.url || "") ? (
              <TextViewer preview={previewFile} />
            ) : (
              <PdfViewer preview={previewFile} />
            )}
          </div>
        </Col>
      </Row>
    </>
  );
};

export default DocumentUploader;
